import styled from 'styled-components';
import { layout, zIndex } from 'tokens';

interface SidebarProps {
	isMobile: boolean;
	sidebarVisible: boolean;
	sidebarDocked: boolean;
	sidebarWidth: number;
}

export const Base = styled.div<SidebarProps>`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100vh; /* fallback for older browsers */
	height: 100svh; /* small viewport height - excludes dynamic UI */
	background-color: ${({ theme }) => theme.white};
	border-right: 1px solid ${({ theme }) => theme.gray[200]};

	${({ isMobile, sidebarVisible, sidebarDocked, sidebarWidth }) =>
		isMobile
			? `
		position: fixed;
		top: 0;
		left: 0;
		width: ${sidebarWidth}px;
		z-index: ${zIndex.sidebar};
		transform: translateX(${sidebarVisible ? '0' : '-100%'});
		transition: transform 0.3s ease;
		box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);
	`
			: sidebarDocked
			? `
		grid-column: 1;
		z-index: ${zIndex.panel};
		transition: none;
	`
			: `
		position: fixed;
		top: 0;
		left: 0;
		width: ${sidebarWidth}px;
		z-index: ${zIndex.sidebar};
		clip-path: inset(0 ${sidebarVisible ? '0%' : '100%'} 0 0);
		transition: clip-path 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
		box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);
	`}
`;

export const DesktopHeader = styled.div`
	height: ${layout.sidebarToggleHeight + 4}px;
	padding-left: ${layout.sidebarToggleWidth + 8}px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-shrink: 0;
	gap: 12px;
	white-space: nowrap;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const DesktopHeaderButton = styled.div`
	display: flex;
	flex-shrink: 0;
	white-space: nowrap;
`;

export const MobileHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 12px 12px 20px;
	flex-shrink: 0;
	gap: 12px;
	white-space: nowrap;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const LogoIconWrapper = styled.div`
	display: flex;
	align-items: center;
	filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.2));
	transition: filter 0.3s ease;
`;

export const MobileHeaderContent = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex: 1;
	transition: transform 0.2s ease;

	&:hover {
		transform: translateX(2px);
	}

	&:hover ${LogoIconWrapper} {
		filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
	}
`;

export const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

export const DockButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	width: 32px;
	margin-right: 8px;
	height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[100]};
	}

	&:active {
		background-color: ${({ theme }) => theme.gray[200]};
	}
`;

export const CloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[100]};
	}

	&:active {
		background-color: ${({ theme }) => theme.gray[200]};
	}
`;

export const Moonchild = styled.div`
	opacity: 0.95;
	padding-bottom: 2px;
`;

export const Content = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow-x: hidden;

	scrollbar-width: thin;
	scrollbar-color: transparent transparent;

	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background-color: transparent;
		border-radius: 3px;
	}

	&:hover {
		scrollbar-color: ${({ theme }) => theme.gray[200]} transparent;

		&::-webkit-scrollbar-thumb {
			background-color: ${({ theme }) => theme.gray[200]};
		}
	}
`;

export const SectionHeader = styled.div`
	font-size: 11px;
	font-weight: 800;
	color: ${({ theme }) => theme.gray[400]};
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin: 20px 12px 16px 18px;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;

	&::after {
		content: '';
		width: 100%;
		height: 1px;
		background: linear-gradient(-90deg, transparent 0%, rgba(156, 163, 175, 0.2) 100%);
		margin-right: 8px;
	}
`;

export const ProjectsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 20px;
	padding: 0 12px;
`;

export const LoadingWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 40px 20px;
`;

export const Footer = styled.div`
	padding: 10px 12px;
	padding-top: 16px;
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	background-color: ${({ theme }) => theme.white};
`;

export const ProfileSettingsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
`;

export const ProfileButton = styled.button`
	flex: 1;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 8px;
	background: transparent;
	border: none;
	border-radius: 6px;
	color: ${({ theme }) => theme.gray[1000]};
	font-size: 13px;
	transition: background 0.2s ease;
	min-width: 0;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
	}
`;

export const ProfileName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 500;
`;

export const SettingsButton = styled.button`
	width: 40px;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	border-radius: 6px;
	transition: all 0.2s ease;
	flex-shrink: 0;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const OrganizationButton = styled.button`
	height: 44px;
	padding: 0 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background: transparent;
	border: none;
	border-radius: 6px;
	transition: all 0.2s ease;
	flex-shrink: 0;
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[600]};
	max-width: 120px;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const OrganizationName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.4);
	z-index: ${zIndex.sidebarOverlay};
	backdrop-filter: blur(2px);
	transition: opacity 0.3s ease;
`;

export const FilterWrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	margin: 8px 12px;
	margin-top: 0px;
	flex-shrink: 0;
`;

export const FilterIcon = styled.div`
	position: absolute;
	left: 12px;
	color: ${({ theme }) => theme.gray[600]};
	pointer-events: none;
	display: flex;
	align-items: center;
`;

export const FilterInput = styled.input`
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 16px;
	font-size: 14px;
	background-color: ${({ theme }) => theme.gray[0]};
	color: ${({ theme }) => theme.gray[1000]};
	transition: border-color 0.2s ease;
	font-family: 'Inter', sans-serif;

	&:focus {
		outline: none;
		border-color: ${({ theme }) => theme.blue[500]};
	}

	&::placeholder {
		color: ${({ theme }) => theme.gray[400]};
	}
`;

export const ViewAllLink = styled.button`
	padding: 10px 16px;
	margin-left: 4px;
	margin-right: 4px;
	background: transparent;
	border: none;
	border-radius: 8px;
	color: ${({ theme }) => theme.gray[700]};
	font-size: 13px;
	font-weight: 400;
	text-align: left;
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	letter-spacing: 0.05em;
	min-height: 10px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: flex;
	align-items: center;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
	}

	&:active {
		background-color: ${({ theme }) => theme.gray[200]};
	}
`;
